import { apiRequest } from '../client'

/**
 * Purchase orders sent to the wholesaler via Bots EDI (CHG-2026-10-edi-purchase-orders): the
 * Standort's "Bestellung" configuration, and the orders of the Lager → Bestellungen page.
 */

/** What keeps a Standort from sending, in the server's fixed order. */
export type OrderingMissing =
  | 'disabled'
  | 'supplier_name'
  | 'supplier_gln'
  | 'edi_recipient'
  | 'buyer_gln'

/** The Standort's "Bestellung" section, read and written with `restaurant:manage`. */
export interface PurchaseOrderingConfig {
  restaurantId: number
  restaurantName: string
  timezone: string
  enabled: boolean
  supplierName: string | null
  supplierGln: string | null
  ediRecipient: string | null
  copyRecipients: Array<string>
  /** `HH:mm` */
  orderCutoffTime: string
  countsForPreordersDefault: boolean
  /** The Standort's webbill pseudo-GLN (`webbill_receiver_ref`), read-only here. */
  buyerGln: string | null
  configured: boolean
  missing: Array<OrderingMissing>
  updatedBy: string | null
  updatedAt: string | null
}

export interface PurchaseOrderingConfigBody {
  enabled: boolean
  supplierName: string | null
  supplierGln: string | null
  ediRecipient: string | null
  copyRecipients: Array<string>
  orderCutoffTime: string
  countsForPreordersDefault: boolean
}

/** The configuration as `purchase_order:view` may read it, for the page header and the Bestellvorschlag. */
export interface OrderingStatus {
  enabled: boolean
  configured: boolean
  missing: Array<OrderingMissing>
  supplierName: string | null
  orderCutoffTime: string
  countsForPreordersDefault: boolean
}

export type PurchaseOrderStatus =
  | 'DRAFT'
  | 'SENDING'
  | 'SENT'
  | 'FAILED'
  | 'DELIVERED'

export type NotOrderableReason =
  | 'unknown_article'
  | 'not_assigned'
  | 'no_stock'
  | 'no_webbill_article'
  | 'article_number_too_long'
  | 'weight_article'
  | 'no_gtin'

/** One package level of a line's article; a level without a GTIN cannot be selected. */
export interface PurchaseOrderLevel {
  level: number
  name: string | null
  gtin: string | null
  contentPerUnit: number
  ediUnit: string
  orderable: boolean
  reason: NotOrderableReason | null
}

export interface PurchaseOrderLine {
  lineNo: number
  sku: string
  name: string | null
  packageLevel: number
  gtin: string | null
  /** The level's name, e.g. "Karton". */
  unitName: string | null
  ediUnit: string
  /** Base units (Stück) per package. */
  contentPerUnit: number
  /** Whole packages of `packageLevel`. */
  quantity: number
  /** `quantity × contentPerUnit`. */
  baseQuantity: number
  supplierArticleNr: string | null
  /** The article's current levels; filled on the detail read and the mutations. */
  levels: Array<PurchaseOrderLevel>
}

export interface NotOrderable {
  sku: string
  name: string | null
  reason: NotOrderableReason
}

export interface PurchaseOrder {
  id: string
  restaurantId: number
  /** Null until the first send. */
  orderNumber: string | null
  status: PurchaseOrderStatus
  /** SENDING for more than 10 minutes: the mail may or may not have gone out. */
  statusUnclear: boolean
  /** ISO date (yyyy-MM-dd). */
  deliveryDate: string
  countsForPreorders: boolean
  note: string | null
  /** On a draft the Standort's configured supplier; once sent, the snapshot of what was sent. */
  supplierName: string | null
  supplierGln: string | null
  buyerGln: string | null
  ediRecipient: string | null
  copyRecipients: Array<string> | null
  failureReason: string | null
  lineCount: number
  createdBy: string | null
  createdAt: string | null
  updatedBy: string | null
  updatedAt: string | null
  sendingStartedAt: string | null
  sentBy: string | null
  sentAt: string | null
  /** The announced delivery the send created. */
  deliveryId: string | null
  deliveryStatus: string | null
  /** Empty in the list. */
  lines: Array<PurchaseOrderLine>
  notOrderable: Array<NotOrderable>
}

/** A Bestellvorschlag row: the Bestellung in base units. */
export interface PurchaseOrderBaseLine {
  sku: string
  baseQuantity: number
}

export interface PurchaseOrderCreateBody {
  deliveryDate: string
  countsForPreorders?: boolean
  note?: string | null
  lines: Array<PurchaseOrderBaseLine>
}

/** `packageLevel` omitted picks the smallest level with a GTIN; `quantity` in whole packages. */
export interface PurchaseOrderWriteLine {
  sku: string
  packageLevel?: number
  quantity: number
}

export interface PurchaseOrderUpdateBody {
  deliveryDate: string
  countsForPreorders?: boolean
  note: string | null
  lines: Array<PurchaseOrderWriteLine>
}

const base = (restaurantId: number) =>
  `/api/v0/admin/inventory/${restaurantId}/purchase-orders`

const configPath = (restaurantId: number) =>
  `/api/v0/admin/restaurants/${restaurantId}/purchase-ordering-config`

export const purchaseOrderingConfigApi = {
  get: (restaurantId: number) =>
    apiRequest<PurchaseOrderingConfig>(configPath(restaurantId)),

  save: (restaurantId: number, body: PurchaseOrderingConfigBody) =>
    apiRequest<PurchaseOrderingConfig>(configPath(restaurantId), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
}

export const purchaseOrdersApi = {
  ordering: (restaurantId: number) =>
    apiRequest<OrderingStatus>(`${base(restaurantId)}/ordering`),

  list: (
    restaurantId: number,
    params: {
      /** Repeated `status` parameters; omit for every status. */
      status?: Array<PurchaseOrderStatus>
      offset?: number
      limit?: number
    } = {},
  ) => {
    const sp = new URLSearchParams()
    for (const s of params.status ?? []) sp.append('status', s)
    if (params.offset != null) sp.set('offset', String(params.offset))
    if (params.limit != null) sp.set('limit', String(params.limit))
    const query = sp.toString()
    return apiRequest<{ items: Array<PurchaseOrder>; total: number }>(
      query ? `${base(restaurantId)}?${query}` : base(restaurantId),
    )
  },

  get: (restaurantId: number, id: string) =>
    apiRequest<PurchaseOrder>(`${base(restaurantId)}/${id}`),

  create: (restaurantId: number, body: PurchaseOrderCreateBody) =>
    apiRequest<PurchaseOrder>(base(restaurantId), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (restaurantId: number, id: string, body: PurchaseOrderUpdateBody) =>
    apiRequest<PurchaseOrder>(`${base(restaurantId)}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  remove: (restaurantId: number, id: string) =>
    apiRequest<void>(`${base(restaurantId)}/${id}`, { method: 'DELETE' }),

  send: (
    restaurantId: number,
    id: string,
    body: { confirmAfterCutoff?: boolean } = {},
  ) =>
    apiRequest<PurchaseOrder>(`${base(restaurantId)}/${id}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}
